import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import PropTypes from "prop-types";
import {
  Box,
  Stack,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  selectFileManagerFilterStatus,
  setFileManagerFilterStatus,
  FileInStatusEnum,
} from "@/features/connections/integrationSlice";
import {
  ShepDataGridPro,
  ShepTooltip,
  ShepButton,
  ShepSearchBar,
  ShepCheckbox,
  ShepPagination,
  ShepHorizontalMenu,
} from "@/components/GenericComponents/ShepComponents";
import LoadingBar from "@/components/shared/LoadingBar";
import { selectIntegrationDateFilter } from "@/features/dateFilterSlice";
import "./FileManagerContent.css";
import axios from "@/features/axiosHelper";
import { FilterDropDownType, UserRoles } from "@/utils/utils";
import CustomFilterDropdown from "@/components/CustomFilterDropdown/CustomFilterDropdown";
import { createAlert } from "@/features/homepage/alertSliceList";
import FileImportPopup from "@/components/FileImport/FileImportPopup";
import {
  importDataTypesEnum,
  importTabTypesEnum,
  setFileImportPopupOpen,
} from "@/features/maintenance/fileImportSlice";
import { fetchUserRole, selectUserRole } from "@/features/auth/userSlice";

import _ from "lodash";
import dayjs from "dayjs";

const FileManagerContent = (props) => {
  const dispatch = useDispatch();

  const [search, setSearch] = useState("");
  const statusFilter = useSelector(selectFileManagerFilterStatus);
  var { startDate, endDate } = useSelector(selectIntegrationDateFilter) || {};
  const [onMount, setOnMount] = useState(false);
  const [refreshGrid, setRefreshGrid] = useState(false);

  const [rows, setRows] = useState([]);
  const [fileLoaded, setIsFileLoaded] = useState(false);
  const [fileDataText, setFileDataText] = useState("");
  const gridRef = useGridApiRef();
  const userRole = useSelector(selectUserRole);
  // Track selected row, update happens only on row click, not checkbox selection, only one selected row each time
  const [fileDetails, setFileDetails] = useState({
    ConnectionId: "",
    ConnectionName: "",
    FileId: "",
    FileName: "",
    Reference: "",
    Status: "",
    FileSize: "",
    FileDataText: "",
    FileData: "",
    CreateDate: "",
    FileInJSONProperties: [],
  });

  const [selectionModel, setSelectionModel] = useState([]); // array to keep track of all of the checkboxes selected

  const dataGridStyle = {
    color: "var(--text-primary)",
    fontSize: "var(--font-sm)",
    fontFamily: "var(--font-family)",
    width: "600px",
    overflowY: "hidden",
    overflowX: "hidden",
    "&, [class^=MuiDataGrid]": { border: "none" },
    "& .MuiDataGrid-columnSeparator": {
      display: "none",
    },
    "& .MuiDataGrid-columnHeaders": {
      display: "none", // column headers not needed to be displayed
    },

    // Display item tool action bar when row is hovered, else shows date
    "& .MuiDataGrid-row:hover": {
      backgroundColor: "var(--tertiary-13)",
    },
    "& .MuiDataGrid-row:hover .item-tools": {
      display: "inline",
    },
    "& .MuiDataGrid-row:hover .receive-date": {
      display: "none",
    },
    "& .MuiDataGrid-row .item-tools": {
      display: "none",
    },
    "& .MuiDataGrid-row .receive-date": {
      display: "inline",
    },

    // Row and checkbox selection
    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus":
      {
        outline: "none",
      },
    "& .Mui-selected, & .MuiDataGrid-row.Mui-selected  ": {
      backgroundColor: "var(--tertiary-13)",
    },
    "& .MuiDataGrid-cellCheckbox": {
      borderLeft: "8px solid transparent",
      borderTopLeftRadius: "4px",
      borderBottomLeftRadius: "4px",
    },
    "& .Mui-selected .MuiDataGrid-cellCheckbox": {
      borderLeft: "8px solid var(--primary-main)",
      borderTopLeftRadius: "4px",
      borderBottomLeftRadius: "4px",
    },
  };

  const schema = [
    {
      id: 0,
      label: "Status",
      type: FilterDropDownType.CheckboxList,
      cascading: true,
      includingAllCheckBox: true,
      childLabel: "Select Status",
      setValue: setFileManagerFilterStatus,
      readVal: selectFileManagerFilterStatus,
      labelKey: "Label",
      uniqueKey: "Value",
    },
  ];

  // Data grid containing file lists and details (Connection name, file name, status, file size, date)
  const columns = [
    {
      field: "fileDetails",
      hide: true,
      flex: 1,
      renderCell: (params) => {
        return (
          <>
            <Box
              direction="row"
              sx={{
                fontSize: "16px",
                marginTop: "15px",
                marginBottom: "15px",
                width: "100%",
              }}
            >
              <Box className="list-header">
                Connection name: {params.row.ConnectionName ?? "Unknown"}
              </Box>
              <Box className="list-header">
                File name: {params.row.FileName ?? "Unknown"}
              </Box>
              <Stack direction="row">
                <Box>
                  <Box display="flex" flexDirection="row">
                    Status: {getStatusName(params.row.Status) ?? "Unknown"}
                  </Box>
                  <Box display="flex" flexDirection="row">
                    File size: {params.row.FileSize ?? "Unknown"} KB{" "}
                  </Box>
                </Box>

                <Stack direction="row" marginLeft="auto">
                  {/* Hover item action toolbar */}
                  <Stack className="item-tools" justifyContent="flex-end">
                    <ShepTooltip title="Download">
                      <IconButton
                        onClick={() =>
                          validateDownload([params.row.FileId], false)
                        }
                      >
                        <div className="file-manager-download"></div>
                      </IconButton>
                    </ShepTooltip>
                    <ShepButton
                      size="small"
                      variant="secondary"
                      sx={{ height: 25, width: 30 }}
                      onClick={() =>
                        retranslateFile([params.row.FileId], false)
                      }
                    >
                      Retranslate
                    </ShepButton>
                  </Stack>

                  <Box className="receive-date" style={{ paddingTop: 15 }}>
                    Date Received:{" "}
                    {convertDate(params.row.CreateDate) ?? "Unknown"}
                  </Box>
                </Stack>
              </Stack>
            </Box>
          </>
        );
      },
    },
  ];

  /**
   * Fetch status filter options depending on if it is on inbound or outbound tab
   */
  const getStatusTypes = () => {
    if (props.isOutbound) {
      axios
        .get("/file/file-out-status")
        .then((response) => {
          dispatch(
            setFileManagerFilterStatus({
              value: response.data,
              heapIOIsInitValue: true,
            }),
          );
        })
        .catch((error) => {
          console.error("Error fetching outbound status: ", error);
        });
    } else {
      axios
        .get("/file/file-in-status")
        .then((response) => {
          dispatch(
            setFileManagerFilterStatus({
              value: response.data,
              heapIOIsInitValue: true,
            }),
          );
        })
        .catch((error) => {
          console.error("Error fetching inbound status: ", error);
        });
    }
  };

  /**
   * Fetch and set selected row's raw file data text
   * @param {string, bool} params: fileId, isOutbound
   */
  const getFileData = (params) => {
    if (params.FileId !== "") {
      axios
        .get("/file/data", {
          params: params,
        })
        .then((response) => {
          if (response != null) {
            setFileDataText(response.data);
          }
        })
        .catch((error) => {
          console.error("Error fetching raw file data: ", error);
        });
    }
  };

  /**
   * Reformat the received date
   * @param {string} dateStr
   */
  const convertDate = (dateStr) => {
    const utcDateStr = dateStr + "Z";
    const date = new Date(utcDateStr);

    const formattedDate = date.toLocaleString({
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: true,
    });

    return formattedDate;
  };

  /**
   * Set the file details and raw file data based on row selected
   * @param selectedRow
   */
  const handleRowSelection = (selectedRow) => {
    if (selectedRow) {
      setFileDetails({
        ConnectionId: selectedRow.ConnectionId,
        ConnectionName: selectedRow.ConnectionName,
        FileId: selectedRow.FileId,
        FileName: selectedRow.FileName,
        Reference: selectedRow.Reference,
        Status: selectedRow.Status,
        FileSize: selectedRow.FileSize,
        FileDataText: fileDataText,
        CreateDate: selectedRow.CreateDate,
        FileInJSONProperties: props.isOutbound
          ? []
          : selectedRow.FileInJSONProperties,
      });
    }
  };

  /**
   * Fetch inbound files list and set row, file details
   * @param params: startDate, endDate, connectionId, statusFilterJson, search
   */
  const getFilesFromServer = (params) => {
    axios
      .get("/file/inbound-files", {
        params: params,
      })
      .then((response) => {
        setFileDetails(response.data);
        setRows(response.data);
        selectFirstFile(response.data);
        setIsFileLoaded(true);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  };

  /**
   * Fetch outbound files list and set row, file details
   * @param params: startDate, endDate, connectionId, statusFilterJson, search
   */
  const getOutboundFilesFromServer = (params) => {
    axios
      .get("/file/outbound-files", {
        params: params,
      })
      .then((response) => {
        setFileDetails(response.data);
        setRows(response.data);
        selectFirstFile(response.data);
        setIsFileLoaded(true);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  };

  // Auto-select and display the first file's details if available
  const selectFirstFile = (responseData) => {
    // ensure that there are files available
    const currentRows = responseData && responseData.length ? responseData : [];
    if (currentRows && currentRows.length) {
      const firstRow = { ...currentRows[0] };
      if (props.isOutbound) {
        firstRow.FileInJSONProperties = [];
      }
      setFileDetails(firstRow);
    }
  };

  const getFiles = (resetCache = false) => {
    if (!startDate || !endDate) return; //block records gathering until datefilter ready
    if (!onMount || fileLoaded) {
      setOnMount(true);
      // Apply date filter;
      const defaultStartDays = 7;

      if (startDate === "" && endDate === "") {
        startDate = dayjs().subtract(defaultStartDays, "day").startOf("day");
        endDate = dayjs().add(1, "day").startOf("day");
      } else if (endDate === null) {
        endDate = startDate;
      }

      const params = prepareGetProperties({
        resetCache: resetCache,
        startDate: startDate,
        endDate: endDate,
        statusFilterJson: statusFilter,
      });

      if (resetCache) {
        setIsFileLoaded(false);
      }
      selectFileType(params);
    }
  };

  const prepareGetProperties = (args) => {
    const params = {
      startDate: args.startDate ?? startDate,
      endDate: args.endDate ?? endDate,
      resetCache: args.resetCache ?? false,
      search: args.search ?? search,
      statusFilterJson: getStatusFilter(args),
    };
    return params;
  };

  const getStatusFilter = (args) => {
    let status = args && args.statusFilterJson;
    if (!status) {
      status = statusFilter;
    }

    let selectedStatuses = status
      .filter((x) => x.isChecked)
      .map((x) => x.Value);

    if (selectedStatuses.includes(FileInStatusEnum.Invalid)) {
      selectedStatuses.push(FileInStatusEnum.Error_at_Re_translating);
    } else if (selectedStatuses.includes(FileInStatusEnum.New)) {
      selectedStatuses.push(FileInStatusEnum.New_from_UI);
    }
    return status ? JSON.stringify(selectedStatuses) : "";
  };

  /**
   * Re-fetch files when the search text field is changed
   * @param event
   * @param data
   */
  const handleTextChange = (event) => {
    const searchVal = event.target.value;
    setSearch(event.target.value);

    const params = prepareGetProperties({ search: searchVal });
    debouncedGetFiles(params);
  };

  const selectFileType = (params) => {
    if (props.isOutbound) {
      getOutboundFilesFromServer(params);
    } else {
      getFilesFromServer(params);
    }
  };

  const debouncedGetFiles = useCallback(
    _.debounce((params) => {
      selectFileType(params);

      // heap.io
      window.heap.track("File Manager - Use Search Bar");
    }, 200),
    [],
  );

  // Get raw file data whenever new row is selected
  useEffect(() => {
    const params = {
      isOutbound: props.isOutbound,
      fileId: fileDetails.FileId,
    };
    if (fileDetails.FileId !== "" && rows.length > 0) {
      getFileData(params);
    }
  }, [fileDetails]);

  //Get files list whenever status filter is changed
  useEffect(() => {
    // When page first loaded (onMount), only make one backend call
    getFiles();
  }, [statusFilter, startDate, endDate]);

  useEffect(() => {
    getStatusTypes();
    dispatch(fetchUserRole());
  }, []);

  const triggerRefresh = () => {
    getFiles(true);
    setRefreshGrid(false);
  };

  // Refreshing the endpoint grid
  useEffect(() => {
    if (refreshGrid) {
      getFiles(true);
      setRefreshGrid(false);
    }
  }, [refreshGrid]);

  const validateDownload = (selectedFiles, heapIOIsBulkAction) => {
    if (selectedFiles.length === 0) {
      dispatch(
        createAlert({
          status: "Error",
          message: "No files selected to download.",
        }),
      );
      return;
    }
    downloadFiles(selectedFiles, heapIOIsBulkAction);
  };

  const getFileName = (files) => {
    let fileName;
    //keep original file name and ext for singular download
    if (files.length === 1) {
      var file = rows.filter((x) => x.FileId == files[0]);
      fileName = file[0].FileName;
    }
    //unique name for zip file with timestamp
    else {
      fileName = `edi_files_${new Date()
        .toISOString()
        .replace(/[:.]/g, "-")}.zip`;
    }
    return fileName;
  };

  const downloadFiles = async (files, heapIOIsBulkAction) => {
    //prevent user from re-clicking download if server response slow
    onSuccess("Your files are being prepared for download.");
    await axios
      .put("/file/download", files, {
        params: {
          isOutbound: props.isOutbound,
        },
        responseType: "blob",
      })
      .then((response) => {
        const fileName = getFileName(files);
        createDownload(response.data, fileName);
        onSuccess("Download complete!");

        // heap.io
        if (heapIOIsBulkAction) {
          window.heap.track("File Manager - Bulk Download");
        } else {
          window.heap.track("File Manager - Download File");
        }
      })
      .catch(async (error) => {
        let errorMessage = await error.response.data.text();
        if (!errorMessage || errorMessage === "{}") {
          errorMessage = "An unexpected error occurred.";
        }
        onError("Error Downloading", errorMessage, "result");
      });
  };

  const createDownload = (data, fileName) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
  };

  const retranslateFile = (selectedFiles, heapIOIsBulkAction) => {
    // retranslate each selected file
    axios
      .put("/file/retranslate", selectedFiles)
      .then((response) => {
        if (response.data.Status) {
          onSuccess(response.data.Message);

          // heap.io
          if (heapIOIsBulkAction) {
            window.heap.track("File Manager - Bulk Retranslate");
          } else {
            window.heap.track("File Manager - Retranslate File");
          }
        } else {
          onError("Error Retranslating", response.data.Message);
        }
        triggerRefresh();
      })
      .catch((error) => {
        console.error("Error", error);
        onError("Error Retranslating", error.response.data.Message);
      });
  };

  const validateDelete = () => {
    console.log("Deleting rows:", selectionModel);
  };

  /* Manually cancel file (only applies to New files uploaded through UI) 
  const cancelFile = async (selectedFiles) => {
    var file = selectedFiles[0];
    console.log("cancel file", file);
    // retranslate each selected file
    await axios
      .post("/fileimport/cancel-edi-file", null, {
        params: { fileId: file },
      })
      .then((response) => {
        // setCancelledFiles([...cancelledFiles, file.FileName]);
        console.log("response", response);
      })
      .catch((e) => {
        console.log("Error: ", e);
        dispatch(
          createAlert({
            status: "Error",
            message: "Unable to cancel import: " + e.response.data,
          })
        );
      });
    triggerRefresh();
  };*/

  const uploadFile = () => {
    dispatch(
      setFileImportPopupOpen({
        open: true,
        tabType: importTabTypesEnum.fileManager,
        dataType: importDataTypesEnum.File_Inbound,
      }),
    );
  };

  // Creates alerts for connection test
  const onSuccess = (message) => {
    dispatch(
      createAlert({
        status: "Success",
        alertType: "single",
        message: message,
      }),
    );
  };

  const onError = (title, message) => {
    dispatch(
      createAlert({
        status: "Error",
        alertType: "result",
        title,
        message,
        buttonLabel: "Close",
      }),
    );
  };

  /**
   * Map the status number to the status label
   * @param statusNum
   * @returns
   */
  const getStatusName = (statusNum) => {
    // error at retranslating status grouped under invalid status
    if (statusNum === FileInStatusEnum.New_from_UI) {
      statusNum = FileInStatusEnum.New;
    } else if (statusNum === FileInStatusEnum.Error_at_Re_translating) {
      statusNum = FileInStatusEnum.Invalid;
    }
    const matchingVal = statusFilter.find((item) => item.Value === statusNum);

    return matchingVal ? matchingVal.Label : "";
  };

  /**
   * File details and raw file data section
   * If no data available, uses default text
   * @returns Component with Connection Name, File Name, Reference, Status, File Size, Date, and File Data Text
   */
  const DocumentView = () => {
    return (
      <>
        <Box
          className="file-manager-content"
          sx={{ display: "block", width: "100%" }}
        >
          <Box className="document-section file-manager-section">
            <Box sx={{ marginBottom: "20px" }}>
              <p className="section-header">
                Connection Name: {fileDetails.ConnectionName ?? "Unknown"}
              </p>
              <p className="section-header">
                File Name: {fileDetails.FileName ?? "Unknown"}
              </p>
            </Box>
            <Box sx={{ marginTop: "20px" }}>
              <p className="sub-header">
                Document Reference Number: {fileDetails.Reference ?? "Unknown"}
              </p>
              <p className="sub-header">
                Status: {getStatusName(fileDetails.Status) ?? "Unknown"}
              </p>
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
              >
                <Box className="sub-header">
                  File size: {fileDetails.FileSize ?? "Unknown"} KB
                </Box>
                <Box className="receive-date">
                  Date Received:{" "}
                  {convertDate(fileDetails.CreateDate) ?? "Unknown"}
                </Box>
              </Box>
            </Box>
          </Box>
          {!props.isOutbound &&
            fileDetails?.FileInJSONProperties?.length > 0 && (
              <Box className="document-section error-section">
                <p className="section-header">Error Details</p>
                <Box sx={{ marginTop: "20px" }}>
                  <List
                    className="remove-list-border"
                    sx={{
                      // Cap the height of error section to show roughly 2-3 items
                      maxHeight: "260px",
                      overflowY: "auto",
                      paddingRight: "8px", // Prevents text from hiding under the scrollbar

                      // scrollbar styling
                      "&::-webkit-scrollbar": {
                        width: "6px",
                      },
                      "&::-webkit-scrollbar-track": {
                        background: "#f1f1f1",
                        borderRadius: "4px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        background: "#c1c1c1",
                        borderRadius: "4px",
                      },
                      "&::-webkit-scrollbar-thumb:hover": {
                        background: "#a8a8a8",
                      },
                    }}
                  >
                    {/* Slice the array to a maximum of 20 items before mapping */}
                    {fileDetails.FileInJSONProperties.slice(0, 20).map(
                      (err, index) => (
                        <ListItem
                          key={index}
                          sx={{ py: 1, alignItems: "flex-start" }}
                        >
                          <ListItemIcon
                            className="listItemIcon"
                            sx={{ mt: 1.5, minWidth: "35px" }}
                          >
                            <span
                              style={{
                                display: "inline-block",
                                width: "9px",
                                height: "9px",
                                borderRadius: "50%",
                                backgroundColor: "red",
                              }}
                            ></span>
                          </ListItemIcon>

                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.5,
                            }}
                          >
                            <Typography variant="subtitle1" fontSize={"19px"}>
                              {err.ErrorMessage}
                            </Typography>
                            {err.LineNumbersWithError &&
                              err.LineNumbersWithError.length > 0 && (
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontSize: "17px",
                                    color: "gray",
                                  }}
                                >
                                  Affected detail level{" "}
                                  {err.LineNumbersWithError.length === 1
                                    ? "line"
                                    : "lines"}
                                  : {err.LineNumbersWithError.join(", ")}
                                </Typography>
                              )}
                            <Typography
                              sx={{ fontSize: "15px", color: "darkgray" }}
                            >
                              {new Date(err.LastUpdated).toLocaleString()}
                            </Typography>
                          </Box>
                        </ListItem>
                      ),
                    )}
                  </List>

                  {/* Show the summary at the bottom only if there are more than 20 errors */}
                  {fileDetails.FileInJSONProperties.length > 20 && (
                    <Typography
                      sx={{
                        marginTop: 2,
                        paddingTop: 1,
                        borderTop: "1px solid #eee",
                        textAlign: "center",
                        color: "text.secondary",
                        fontSize: "14px",
                        fontStyle: "italic",
                      }}
                    >
                      Showing 20 out of{" "}
                      {fileDetails.FileInJSONProperties.length} errors.
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          <Box className="document-section file-manager-section column">
            <div className="section-header">Raw Document Data</div>
            <div style={{ whiteSpace: "pre-wrap", overflowWrap: "break-word" }}>
              <p>
                {fileDataText !== "" ? fileDataText : "No file data to display"}
              </p>
            </div>
          </Box>
        </Box>
      </>
    );
  };

  const actions = (
    userRole === UserRoles.SuperAdmin
      ? [{ label: "Upload", onClick: () => uploadFile({}) }]
      : []
  ).concat([
    {
      label: "Download",
      onClick: () => validateDownload(selectionModel, true),
    },
    {
      label: "Re-translate",
      onClick: () => retranslateFile(selectionModel, true),
    },

    // placeholder, currently not available as an action outside of upload popup
    //{ label: "Cancel", onClick: () => cancelFile(selectionModel) },

    //Delete currently not supported but will be in the future
    // { label: "Delete", onClick: () => validateDelete({}) },
  ]);

  return (
    <Box sx={{ display: "block", flexDirection: "column" }}>
      <Box style={{ display: "flex", paddingBottom: 5 }}>
        <CustomFilterDropdown schemas={schema} />
        <ShepSearchBar
          placeholder="Search"
          value={search}
          onChange={handleTextChange}
          sx={{
            width: "562px",
            paddingLeft: "10px",
            "&.MuiFormControl-root .MuiInputBase-root": {
              borderRadius: "5px",
            },
          }}
        />
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <ShepHorizontalMenu actions={actions} />
        </div>
      </Box>
      <FileImportPopup
        onClose={triggerRefresh}
        onUploadSuccess={triggerRefresh}
      />

      {/* Files list data grid */}
      <Box
        className="content-box"
        sx={{ display: "flex", flexDirection: "row", marginTop: "20px" }}
      >
        <Box className="file-list-section">
          <div style={{ height: 800, flex: 1, margin: 1 }}>
            <ShepDataGridPro
              apiRef={gridRef}
              isDataLoaded={fileLoaded}
              rowHeight={120}
              secondsToStall={4}
              autoHeight={false}
              rows={rows}
              getRowId={(row) => row.FileId}
              columns={columns}
              checkboxSelection
              disableMultipleRowSelection={true} // row is single select, checkbox is multi-select
              pagination
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 15, page: 0 },
                },
              }}
              // updateFileDetails if row or checkbox is selected, but if the row is selected, don't auto-toggle the checkbox
              onCellClick={(params, e) => {
                if (params.field !== "__check__") {
                  // stop the checkbox from being toggled whenever the row is selected
                  e.stopPropagation();
                }

                handleRowSelection(params.row);
              }}
              // logic for handling selection change. If there are multiple rows selected, selection state updated by adding or removing rows
              onRowSelectionModelChange={(newSelection) => {
                // update checkbox selection model for checkbox clicks
                setSelectionModel(newSelection);
                // console.debug("selectionModel", selectionModel, newSelection);
              }}
              getRowClassName={(params) => {
                // Style class for highlighting the selected row
                if (params.id === fileDetails.FileId) {
                  return "highlight-row";
                }
                return "";
              }}
              slots={{
                baseCheckbox: ShepCheckbox,
                pagination: ShepPagination,
              }}
              sx={dataGridStyle}
            />
          </div>
        </Box>

        {rows.length !== 0 ? (
          <DocumentView />
        ) : (
          <Box sx={{ width: "100%" }}>
            <Box className="document-section file-manager-section">
              <p className="section-header">No files available</p>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

FileManagerContent.propTypes = {
  isOutbound: PropTypes.bool,
};

FileManagerContent.defaultProps = {
  isOutbound: false,
};

export default FileManagerContent;
